import { ChatState } from '@enuan/chat__utils';
import { styled } from '@mui/material/styles';
import React, { useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ReactComponent as TogglerInnerShape } from '@/assets/floater-inner-shape.svg';
import { configurationContext } from '../../contexts/configuration';
import { chatTogglerMessages } from './chatToggler.i18n';

type TogglerButtonIconProps = {
	className?: string;
};

/**
 * Renders the toggler floater icon for the current chat state.
 *
 * Resolves `icon_active` / `icon_inactive` from configuration:
 * `false` hides the icon, a `string` shows a custom image, and `undefined`
 * falls back to the built-in SVG default.
 */
export const TogglerButtonIcon = styled(
	({ className }: TogglerButtonIconProps) => {
		const { toggler_button, state } = useContext(configurationContext);

		const { icon_active, icon_inactive } = toggler_button ?? {};

		const intl = useIntl();
		const title =
			state === ChatState.ON
				? intl.formatMessage(chatTogglerMessages.tooltip_active)
				: intl.formatMessage(chatTogglerMessages.tooltip_inactive);

		/**
		 * Resolves the icon element for the current chat state.
		 *
		 * Three possible outcomes:
		 * - `false` value  → explicit "no icon" request; returns `null`.
		 * - `string` value → custom uploaded image; returns an `<img>`.
		 * - `undefined`    → no custom icon set; falls back to the built-in SVG default.
		 */
		const icon = useMemo(() => {
			const value = state === ChatState.ON ? icon_active : icon_inactive;
			if (value === false) return null;
			if (typeof value === 'string') return <img src={value} alt={title} />;
			return <TogglerInnerShape height="100%" width="100%" />;
		}, [state, icon_active, icon_inactive, title]);

		return (
			<div className={className}>
				<div className="floater__icon">{icon}</div>
				<div className="floater__dot" />
			</div>
		);
	},
	{ label: 'TogglerButtonIcon', target: 'TogglerButtonIcon-root' },
)({
	display: 'contents',
	'& .floater__icon': {
		width: '60%',
		position: 'absolute',
		pointerEvents: 'none',
		'& img': {
			width: '100%',
			height: 'auto',
			maxWidth: '6.36rem',
			maxHeight: '6.36rem',
		},
		'& svg': {
			overflow: 'visible',
			boxSizing: 'content-box',
		},
		transition: '0.5s',
		transitionTimingFunction: 'linear',
		transformOrigin: 'bottom right',
		transitionProperty: 'all',
		'& .group1, & .group2': {
			transition: '0.4s',
			transitionTimingFunction: 'linear',
			transitionProperty: 'fill',
		},
	},
	'& .floater__dot': {
		alignSelf: 'flex-end',
		position: 'absolute',
		borderRadius: '50%',
		backgroundColor: 'var(--toggler__dot_background)',
		transform: 'scale(0)',
		transition: '0.5s',
		transitionTimingFunction: 'linear',
		transformOrigin: 'bottom right',
	},
});
